import "./TickerExpandedImage.sass";
import { memo, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BANNER_MOVE } from "./tickerBannerUtils";
import { ANIMATED_BANNERS_START_DELAY } from "../constants-ticker";

/**
 * Duración (segundos) usada como respaldo cuando un item no trae `duration`.
 */
const DEFAULT_ITEM_DURATION = 5;

/**
 * Transición común, suave, para las entradas/salidas de los banners.
 */
const SMOOTH_TRANSITION = { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] };

/**
 * Escala inicial de las animaciones de expansión. Un valor cercano a 1 hace que
 * el crecimiento sea sutil (en lugar de expandirse desde cero).
 */
const SUBTLE_SCALE_FROM = 0.94;

/**
 * Construye los estilos de dimensión a partir del item.
 *
 * @param {import("./tickerBannerUtils").BannerImageItem} item
 * @param {{ includeWidth?: boolean, includeHeight?: boolean }} [options]
 * @returns {Object} Estilos inline (px) para width/height cuando existan.
 */
function getSizeStyle(item, { includeWidth = true, includeHeight = true } = {}) {
  const style = {};
  if (includeWidth && item?.width) style.width = `${item.width}px`;
  if (includeHeight && item?.height) style.height = `${item.height}px`;
  return style;
}

/**
 * Modo "expandir izquierda": el banner se expande horizontalmente (scaleX).
 * Ocupa el ancho disponible y se muestra por encima del slot fijo.
 * El alto llega desde el JSON.
 */
function ExpandLeftBanner({ item }) {
  return (
    <motion.div
      className="ticker-expanded-image ticker-expanded-image--left"
      style={getSizeStyle(item, { includeWidth: false })}
      initial={{ opacity: 0, scaleX: SUBTLE_SCALE_FROM }}
      animate={{ opacity: 1, scaleX: 1 }}
      exit={{ opacity: 0, scaleX: SUBTLE_SCALE_FROM }}
      transition={SMOOTH_TRANSITION}
    >
      <img
        src={item.url}
        alt={item.name || "ticker-image-banner"}
        className="ticker-expanded-image__asset"
      />
    </motion.div>
  );
}

/**
 * Modo "expandir arriba": el banner se expande verticalmente (scaleY) y se
 * muestra por encima del slot fijo. Ancho y alto se toman del JSON.
 */
function ExpandUpBanner({ item }) {
  return (
    <motion.div
      className="ticker-expanded-image ticker-expanded-image--up"
      style={getSizeStyle(item)}
      initial={{ opacity: 0, scaleY: SUBTLE_SCALE_FROM }}
      animate={{ opacity: 1, scaleY: 1 }}
      exit={{ opacity: 0, scaleY: SUBTLE_SCALE_FROM }}
      transition={SMOOTH_TRANSITION}
    >
      <img
        src={item.url}
        alt={item.name || "ticker-image-banner"}
        className="ticker-expanded-image__asset"
      />
    </motion.div>
  );
}

/**
 * Slot fijo PERMANENTE: siempre montado (su espacio queda reservado). Solo se
 * reemplaza la imagen con un crossfade suave. `mode="wait"` evita que se vean
 * dos imágenes a la vez (primero sale la anterior, luego entra la nueva).
 * Ancho y alto se toman del JSON.
 */
function FixedSlot({ item }) {
  if (!item?.url) return null;

  return (
    <div
      className="ticker-expanded-image ticker-expanded-image--fixed"
      style={getSizeStyle(item)}
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={item.url}
          src={item.url}
          alt={item.name || "ticker-image"}
          className="ticker-expanded-image__asset ticker-expanded-image__asset--stacked"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={SMOOTH_TRANSITION}
        />
      </AnimatePresence>
    </div>
  );
}

/**
 * Renderiza un banner animado (expandir izquierda / arriba) según su modo.
 *
 * @param {{ item: import("./tickerBannerUtils").BannerImageItem }} props
 */
function AnimatedBannerItem({ item }) {
  switch (item.move) {
    case BANNER_MOVE.EXPAND_UP:
      return <ExpandUpBanner item={item} />;
    case BANNER_MOVE.EXPAND_LEFT:
    default:
      return <ExpandLeftBanner item={item} />;
  }
}

/**
 * TickerExpandedImage
 *
 * Reproduce los banners publicitarios en un ÚNICO ciclo ordenado: se muestra un
 * banner a la vez, respetando el orden de la playlist. Cada banner permanece
 * visible el número de segundos indicado por su `duration` y se visualiza según
 * su `move`:
 *  - "fijo": imagen estática con fade.
 *  - "expandir izquierda": se expande horizontalmente (scaleX).
 *  - "expandir arriba": se expande verticalmente (scaleY), puede desbordar.
 *
 * Al mostrarse un solo banner por turno, el banner fijo también es visible
 * cuando le corresponde (no queda tapado por los animados).
 *
 * @param {Object} props
 * @param {import("./tickerBannerUtils").BannerImageItem[]} props.playlist
 *        Items de imagen aplanados y aleatorizados de todos los banners.
 * @param {(move: string|null) => void} [props.onMoveChange]
 *        Notifica al padre el `move` del banner visible (o null).
 *        Permite ajustar el contenedor cuando el banner desborda (ej. "expandir arriba").
 * @param {number} [props.animatedStartDelay]
 *        Retraso (ms) antes de empezar a mostrar los banners animados
 *        ("expandir izquierda" / "arriba"). Durante ese tiempo solo se ve el fijo.
 */
function TickerExpandedImage({
  playlist = [],
  onMoveChange,
  animatedStartDelay = ANIMATED_BANNERS_START_DELAY,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Los banners animados solo se habilitan tras `animatedStartDelay`.
  const [animatedEnabled, setAnimatedEnabled] = useState(animatedStartDelay <= 0);

  // Activa los banners animados al cumplirse el retraso inicial.
  useEffect(() => {
    if (animatedStartDelay <= 0) {
      setAnimatedEnabled(true);
      return undefined;
    }

    setAnimatedEnabled(false);
    const timer = window.setTimeout(() => setAnimatedEnabled(true), animatedStartDelay);
    return () => window.clearTimeout(timer);
  }, [animatedStartDelay, playlist]);

  // Playlist activa: durante el retraso solo rotan los banners fijos; una vez
  // habilitados los animados, rota la playlist completa.
  const activePlaylist = useMemo(() => {
    const items = Array.isArray(playlist) ? playlist : [];
    if (animatedEnabled) return items;
    return items.filter((item) => item.move === BANNER_MOVE.FIXED);
  }, [playlist, animatedEnabled]);

  // Reinicia el índice al cambiar la playlist (refresco del servicio).
  useEffect(() => {
    setCurrentIndex(0);
  }, [playlist]);

  // Reinicia el índice al cambiar la lista activa (retraso de animados cumplido).
  useEffect(() => {
    setCurrentIndex(0);
  }, [activePlaylist]);

  useEffect(() => {
    if (activePlaylist.length === 0) return;

    const current = activePlaylist[currentIndex] ?? activePlaylist[0];
    const seconds = current?.duration > 0 ? current.duration : DEFAULT_ITEM_DURATION;

    const timer = window.setTimeout(() => {
      // Avanza cíclicamente; al completar la vuelta vuelve a mostrar todos.
      setCurrentIndex((prev) => (prev + 1) % activePlaylist.length);
    }, seconds * 1000);

    return () => window.clearTimeout(timer);
  }, [currentIndex, activePlaylist]);

  const hasItems = activePlaylist.length > 0;
  const currentItem = hasItems ? (activePlaylist[currentIndex] ?? activePlaylist[0]) : null;
  const isFixedTurn = currentItem?.move === BANNER_MOVE.FIXED;

  // Informa al padre el modo del banner visible (para el overflow del "up").
  useEffect(() => {
    if (typeof onMoveChange === "function") {
      onMoveChange(currentItem?.url ? currentItem.move : null);
    }
  }, [currentItem, onMoveChange]);

  // No hay banner en el turno actual: nada que mostrar.
  if (!currentItem?.url) return null;

  // Banner animado del turno actual (solo si NO es turno del fijo).
  const animatedItem = !isFixedTurn ? currentItem : null;

  // Se muestra UN SOLO banner a la vez, según el turno actual:
  //  - turno "fijo": solo el slot fijo.
  //  - turno animado ("expandir izquierda"/"arriba"): solo ese banner.
  // Nunca se solapan dos banners simultáneamente.
  return (
    <>
      {isFixedTurn && <FixedSlot item={currentItem} />}

      <AnimatePresence mode="wait">
        {animatedItem && (
          <AnimatedBannerItem
            key={`${animatedItem.url}-${currentIndex}`}
            item={animatedItem}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default memo(TickerExpandedImage);
